import React, { useLayoutEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import CitiesPage from "./pages/CitiesPage";
import PlacesPage from "./pages/PlacesPage";
import DirectionsPage from "./pages/DirectionsPage";
import TicketSearchLoadingPage from "./pages/Tickets/TicketSearchLoadingPage";
import TicketDriverPage from "./pages/Tickets/TicketDriverPage";
import ContactPage from "./pages/ContactPage";
import GuidesListPage from "./pages/GuidesList";
import GuideDetailPage from "./pages/GuidesDetail/GuideDetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RegionDetail from "./pages/GuidesDetail/RegionDetail";
import PopularPlacesPage from "./pages/PopularPlacesPage";
import HowItWorks from "./pages/HowItWorks";
import SpotDetail from "./pages/GuidesDetail/SpotDetail";
import DashboardPage from "./pages/DashboardPage";
import RideHistoryPage from "./pages/RideHistoryPage";
import ProfilePage from "./pages/ProfilePage";
import WorkSelectionPage from "./pages/WorkSelectionPage";
import ProviderPortalPage from "./pages/ProviderPortalPage";
import ProtectedRoute from "./components/ProtectedRoute";
import AccountPreferenceSync from "./components/AccountPreferenceSync";
import BusLoading from "./components/BusLoading";

function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function TicketOrderRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/tickets${search}`} replace />;
}

function AppRoutes() {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();
  const portalType = process.env.REACT_APP_PORTAL_TYPE;
  if (portalType === "driver" || portalType === "guide") return <ProviderPortalPage portalType={portalType} />;

  const isRegionDetail = pathname.startsWith("/regions/");
  const isTicketSearchLoading = pathname === "/tickets/searching";
  const isTicketOrderPage = pathname === "/tickets" || pathname === "/tickets/driver";
  const isRestrictedProvider = (user?.role === "driver" || user?.role === "guide") && !user?.roles?.includes("client");
  const isProviderAllowedPath = pathname === "/dashboard" || pathname === "/profile" || pathname.startsWith("/guides/");

  if (isRestrictedProvider && !isProviderAllowedPath) return <Navigate to="/dashboard" replace />;
  if (loading && pathname !== "/dashboard") return <BusLoading className="bg-white dark:bg-slate-950" />;

  return (
    <>
      <ScrollToTop />
      {!loading && !isRegionDetail && !isTicketSearchLoading && !isTicketOrderPage && <Navbar />}

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/cities" element={<CitiesPage />} />
        <Route path="/guides" element={<GuidesListPage />} />
        <Route path="/guides/:guideId" element={<GuideDetailPage />} />
        <Route path="/places" element={<PlacesPage />} />
        <Route path="/tickets" element={<ProtectedRoute><TicketDriverPage /></ProtectedRoute>} />
        <Route path="/tickets/searching" element={<ProtectedRoute><TicketSearchLoadingPage /></ProtectedRoute>} />
        <Route path="/tickets/results" element={<ProtectedRoute><TicketOrderRedirect /></ProtectedRoute>} />
        <Route path="/tickets/catalog" element={<ProtectedRoute><TicketOrderRedirect /></ProtectedRoute>} />
        <Route path="/tickets/driver" element={<ProtectedRoute><TicketDriverPage /></ProtectedRoute>} />
        <Route path="/directions" element={<DirectionsPage />} />
        <Route path="/routes" element={<Navigate to="/tickets" replace />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/regions/:slug/places" element={<PopularPlacesPage />} />
        <Route path="/regions/:slug" element={<RegionDetail />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/spots/:slug" element={<SpotDetail />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/dashboard/ride-history" element={<ProtectedRoute><RideHistoryPage /></ProtectedRoute>} />
        <Route path="/work" element={<ProtectedRoute><WorkSelectionPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/profile/:profileId" element={<ProfilePage />} />
        <Route path="*" element={<Navigate to={isRestrictedProvider ? "/dashboard" : "/"} replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AccountPreferenceSync />
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}
